import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { X, ExternalLink, MapPin, User } from "lucide-react";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";

interface SiteProperty {
  id: string;
  property_name: string;
  location: string | null;
  asking_price: string | null;
  date: string | null;
  summary_details: string | null;
  why_we_like_it: string | null;
  plan_description: string | null;
  high_level_finances: string | null;
  website_link_1: string | null;
  website_link_2: string | null;
  google_maps_address: string | null;
  selling_agent: string | null;
  pdf_url_1: string | null;
  pdf_label_1: string | null;
  pdf_url_2: string | null;
  pdf_label_2: string | null;
  pdf_url_3: string | null;
  pdf_label_3: string | null;
}

interface SitePipelineWidgetProps {
  projectId: string;
}

export default function SitePipelineWidget({
  projectId,
}: SitePipelineWidgetProps) {
  const [properties, setProperties] = useState<SiteProperty[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<SiteProperty | null>(
    null
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchProperties() {
      try {
        const { data, error } = await supabase
          .from("site_pipeline")
          .select("*")
          .eq("project_id", projectId)
          .order("date", { ascending: false });

        if (error) throw error;
        setProperties(data || []);
      } catch (error) {
        console.error("Error fetching site pipeline:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchProperties();
  }, [projectId]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Card variant="bordered">
        <CardHeader color="indigo">
          <h3 className="text-lg sm:text-xl font-bold text-black">
            Site Pipeline
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Sourced sites with potential for deployment
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (properties.length === 0) {
    return null;
  }

  return (
    <>
      <Card variant="bordered">
        <CardHeader color="indigo">
          <h3 className="text-lg sm:text-xl font-bold text-black">
            Site Pipeline
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Sourced sites with potential for deployment
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {properties.map((property) => (
              <div
                key={property.id}
                onClick={() => setSelectedProperty(property)}
                className="p-4 border-2 border-black rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="font-bold text-lg mb-2">
                  {property.property_name}
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>Location: {property.location || "N/A"}</div>
                  <div>Price: {property.asking_price || "N/A"}</div>
                  <div>Date: {formatDate(property.date)}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {selectedProperty && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProperty(null)}
        >
          <div
            className="bg-white rounded-lg border-4 border-black max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-indigo-400 border-b-4 border-black p-6 flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-black text-black mb-2">
                  {selectedProperty.property_name}
                </h2>
                <div className="flex flex-wrap gap-4 text-sm text-black">
                  {selectedProperty.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span className="font-bold">
                        {selectedProperty.location}
                      </span>
                    </div>
                  )}
                  {selectedProperty.asking_price && (
                    <span className="font-black">
                      {selectedProperty.asking_price}
                    </span>
                  )}
                  {selectedProperty.date && (
                    <span className="font-bold">
                      {formatDate(selectedProperty.date)}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedProperty(null)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors ml-4"
              >
                <X className="w-6 h-6 text-black" strokeWidth={3} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {(selectedProperty.website_link_1 ||
                selectedProperty.website_link_2 ||
                selectedProperty.google_maps_address ||
                selectedProperty.selling_agent) && (
                <div className="space-y-3">
                  {selectedProperty.website_link_1 && (
                    <a
                      href={selectedProperty.website_link_1}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 border-2 border-black rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <ExternalLink className="w-5 h-5" />
                      <span className="font-bold">Website 1</span>
                    </a>
                  )}
                  {selectedProperty.website_link_2 && (
                    <a
                      href={selectedProperty.website_link_2}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 border-2 border-black rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <ExternalLink className="w-5 h-5" />
                      <span className="font-bold">Website 2</span>
                    </a>
                  )}
                  {selectedProperty.google_maps_address && (
                    <a
                      href={selectedProperty.google_maps_address}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 border-2 border-black rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <MapPin className="w-5 h-5" />
                      <span className="font-bold">View on Google Maps</span>
                    </a>
                  )}
                  {selectedProperty.selling_agent && (
                    <div className="flex items-center gap-2 p-3 border-2 border-black rounded-lg bg-gray-50">
                      <User className="w-5 h-5" />
                      <div>
                        <div className="text-xs text-gray-600">
                          Selling Agent
                        </div>
                        <div className="font-bold">
                          {selectedProperty.selling_agent}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedProperty.summary_details && (
                <div>
                  <h3 className="text-lg font-black text-black mb-2">
                    Summary Details
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                    {selectedProperty.summary_details}
                  </p>
                </div>
              )}

              {selectedProperty.why_we_like_it && (
                <div>
                  <h3 className="text-lg font-black text-black mb-2">
                    Why We Like It
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                    {selectedProperty.why_we_like_it}
                  </p>
                </div>
              )}

              {selectedProperty.plan_description && (
                <div>
                  <h3 className="text-lg font-black text-black mb-2">
                    What the Plan Would Look Like
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                    {selectedProperty.plan_description}
                  </p>
                </div>
              )}

              {selectedProperty.high_level_finances && (
                <div>
                  <h3 className="text-lg font-black text-black mb-2">
                    High Level Finances
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                    {selectedProperty.high_level_finances}
                  </p>
                </div>
              )}

              {(selectedProperty.pdf_url_1 ||
                selectedProperty.pdf_url_2 ||
                selectedProperty.pdf_url_3) && (
                <div>
                  <h3 className="text-lg font-black text-black mb-3">
                    Documents
                  </h3>
                  <div className="space-y-2">
                    {selectedProperty.pdf_url_1 && (
                      <a
                        href={selectedProperty.pdf_url_1}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 border-2 border-black rounded-lg hover:bg-indigo-50 transition-colors"
                      >
                        <ExternalLink className="w-5 h-5" />
                        <span className="font-bold">
                          {selectedProperty.pdf_label_1 || "Document 1"}
                        </span>
                      </a>
                    )}
                    {selectedProperty.pdf_url_2 && (
                      <a
                        href={selectedProperty.pdf_url_2}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 border-2 border-black rounded-lg hover:bg-indigo-50 transition-colors"
                      >
                        <ExternalLink className="w-5 h-5" />
                        <span className="font-bold">
                          {selectedProperty.pdf_label_2 || "Document 2"}
                        </span>
                      </a>
                    )}
                    {selectedProperty.pdf_url_3 && (
                      <a
                        href={selectedProperty.pdf_url_3}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 border-2 border-black rounded-lg hover:bg-indigo-50 transition-colors"
                      >
                        <ExternalLink className="w-5 h-5" />
                        <span className="font-bold">
                          {selectedProperty.pdf_label_3 || "Document 3"}
                        </span>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
